import type { GetAbsoluteUrlOptions } from '@segment/utils/getAbsoluteUrl'
import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import The4StepGuide from './assets/The4StepGuide.png'
import WhatDoesItTake from './assets/WhatDoesItTake.png'
import JumpstartLite from './assets/JumpstartLite.png'
import AnalyticsAcademy from './assets/AnalyticsAcademy.png'
import CustomerStoryIbm from './assets/CustomerStoryIbm.png'
import DefaultThumbnail2 from './assets/DefaultThumbnail2.png'
import StreamlineCompliance from './assets/StreamlineCompliance.png'
import DefaultThumbnail from './assets/DefaultThumbnail.png'
import CustomerStoryFender from './assets/CustomerStoryFender.png'
import IdentityResolutionGuide from './assets/IdentityResolutionGuide.png'
import CustomerStoryToggle from './assets/CustomerStoryToggle.png'
import CustomerStoryEndeavor from './assets/CustomerStoryEndeavor.png'
import CustomerStoryDominos from './assets/CustomerStoryDominos.png'
import CustomerStoryAllergan from './assets/CustomerStoryAllergan.png'
import AuthorLogo from './assets/AuthorLogo.png'

type ResourceType = `Blog` | `Recipe` | `Guide` | `Segment University` | `Analytics Academy` | `Customer Story`

interface ResourceProps {
  asset: string
  type: ResourceType
  categories?: string[]
  title: string
  excerpt: string
  authorLogo?: string
  authorName?: string
  button: string
  href: string
}

export interface ResourcesProps {
  title: string
  resources: ResourceProps[]
}

const LEARN_URL = `https://learn.segment.com`
const UNIVERSITY_URL = `https://university.segment.com`
const TWILIO_URL = `https://www.twilio.com`

const data = (pageConfig: GetAbsoluteUrlOptions): ResourcesProps[] => {
  return [
    {
      title: `Introduction`,
      resources: [
        {
          asset: The4StepGuide,
          type: `Guide`,
          categories: [`Customer Data Platform (CDP)`, `Marketing`, `Growth`, `Analytics`],
          title: `The 4-Step Guide to CDP Success`,
          excerpt: `Managing a customer data platform project can feel like a treacherous mountain expedition. To meet the business and technical requirements involved with your CDP project you need a comprehensive plan for implementation to ensure success. Our new guide makes the journey easier by con...`,
          button: `Read it here`,
          href: getAbsoluteUrl(`/guide-to-cdp-success/`, {
            baseUrl: LEARN_URL,
            basePath: pageConfig.basePath,
          }),
        },
        {
          asset: WhatDoesItTake,
          type: `Blog`,
          categories: [`Growth & Marketing`],
          title: `What Does it Take to Achieve Good Data at Enterprise Scale?`,
          excerpt: `In this post, you’ll learn why data is worth standardizing, two models of ownership for driving data standards at your company, and how to stay agile in the process.`,
          button: `Read it here`,
          href: getAbsoluteUrl(
            `https://segment.com/blog/what-does-it-take-to-achieve-good-data-at-enterprise-scale/`,
            pageConfig
          ),
        },
      ],
    },
    {
      title: `Foundational Maturity`,
      resources: [
        {
          asset: JumpstartLite,
          type: `Segment University`,
          title: `Jumpstart Lite for Existing Segment Customers`,
          excerpt: `For hands-on practice, we recommend trying the “Goals & Use-Cases” activity in our Jumpstart Lite program (for existing Segment Customers on Segment University).`,
          button: `Find it here`,
          href: getAbsoluteUrl(`/jumpstart-lite-for-existing-segment-customers/`, {
            baseUrl: UNIVERSITY_URL,
            basePath: pageConfig.basePath,
          }),
        },
        {
          asset: AnalyticsAcademy,
          type: `Analytics Academy`,
          title: `Choosing Metrics that Matter`,
          excerpt: `Everyone loves their wall-mounted dashboards, but we rarely pick the right metrics to track. And tracking the wrong metrics is almost as bad as tracking nothing at all. Here's what you need to know about selecting actionable metrics.`,
          button: `Read it here`,
          href: getAbsoluteUrl(`/academy/intro/choosing-metrics-that-matter/`, pageConfig),
        },
        {
          asset: CustomerStoryIbm,
          type: `Customer Story`,
          title: `How IBM Scaled its Customer Data Foundation Across 150 Products`,
          excerpt: `This case study highlights how IBM partnered with Twilio Segment to scale its customer data infrastructure and drive increased customer adoption, engagement and expansion.`,
          button: `Read their story`,
          href: getAbsoluteUrl(`/customers/ibm/`, pageConfig),
        },
        {
          asset: DefaultThumbnail2,
          type: `Blog`,
          title: `First Party Data: How to Activate & Leverage Your Data`,
          excerpt: `We outline a path to building trust in data across your organization, and explain why it's critical to lay the right infrastructure and process for how data is collected, cleaned, governed, and acted on.`,
          button: `Read it here`,
          href: getAbsoluteUrl(`https://segment.com/blog/first-party-data-guide/`, pageConfig),
        },
        {
          asset: StreamlineCompliance,
          type: `Recipe`,
          categories: [`Startup`, `Mobile`, `Marketplace`, `Media`, `B2B`],
          title: `Streamline Compliance with End-User Privacy Tools`,
          excerpt: `In this recipe, you’ll learn how to save time complying with user deletion and suppression requests by using Segment’s deletion features.`,
          authorLogo: AuthorLogo,
          authorName: `Made by Segment`,
          button: `Use this recipe`,
          href: getAbsoluteUrl(`/recipes/streamline-GDPR-compliance-with-Segment-end-user-privacy-tools/`, pageConfig),
        },
        {
          asset: DefaultThumbnail,
          type: `Blog`,
          categories: [`Identity Management`],
          title: `Identity Resolution: The Definitive Guide`,
          excerpt: `At Segment, our customer data platform has helped thousands of businesses merge the complete history of each customer into a single profile. That’s why we’ve put together this short guide, to help you understand the nuts and bolts of identity resolution, and how you can bring it into your organization.`,
          button: `Read it here`,
          href: getAbsoluteUrl(`https://segment.com/blog/identity-resolution/`, pageConfig),
        },
        {
          asset: CustomerStoryFender,
          type: `Customer Story`,
          title: `How Fender Improves Customer Engagement with Quality Data`,
          excerpt: `Fender turned to Twilio Segment to create a single source of truth by unifying customer data. Now Fender's marketing and product teams can query events and traits with Segment’s self-service audience builder and create cohorts of users for more target messaging. And API access to user profiles...`,
          button: `Read their story`,
          href: getAbsoluteUrl(`/customers/fender/`, pageConfig),
        },
        {
          asset: IdentityResolutionGuide,
          type: `Guide`,
          categories: [`Analytics`, `Customer Data Platform (CDP)`, `Marketing`, `Growth`],
          title: `Identity Resolution: A Guide to the Post-Cookie World`,
          excerpt: `Due to growing consumer expectations for personalization and privacy, companies need to take a hard look at identity resolution. This guide walks you through the common challenges and required capabilities of a successful first-party identity resolution strategy.`,
          button: `Read it here`,
          href: getAbsoluteUrl(`/identity-resolution/`, {
            baseUrl: LEARN_URL,
            basePath: pageConfig.basePath,
          }),
        },
        {
          asset: CustomerStoryToggle,
          type: `Customer Story`,
          title: `How Toggle Speeds into New Markets with Connected Data`,
          excerpt: `With the launch of Toggle (a Farmers Insurance company) the technology, engineering and marketing teams built a modern, future-proofed technology stack, with Twilio Segment Customer Data Platform at its core. Twilio Segment empowers the brand to move quickly and iterate, enabling...`,
          button: `Read their story`,
          href: getAbsoluteUrl(`/customers/toggle/`, pageConfig),
        },
      ],
    },
    {
      title: `Advancing Maturity`,
      resources: [
        {
          asset: DefaultThumbnail,
          type: `Blog`,
          title: `Personalize Your Twilio IVR with Segment`,
          excerpt: `This blog covers how, with Segment, it has never been easier to obtain, aggregate, and act on customer interaction with any product your company provides. You can use this information to improve your product, send tailored marketing messages, or tailor your customer service to your customers' needs.`,
          button: `Read it here`,
          href: getAbsoluteUrl(`https://segment.com/blog/personalize-ivr-segment-flex/`, {
            baseUrl: TWILIO_URL,
            basePath: pageConfig.basePath,
          }),
        },
      ],
    },
    {
      title: `Adaptive Maturity`,
      resources: [
        {
          asset: DefaultThumbnail2,
          type: `Blog`,
          title: `Build Quick Customer Audiences with RFM Modelling`,
          excerpt: `The term 'RFM' stands for Recency, Frequency, and Monetary Value Tracking. These three fairly straightforward characteristics allow you to quickly build complex models about how your customers relate to your brand, and how you should engage with them.`,
          button: `Read more`,
          href: getAbsoluteUrl(`https://segment.com/blog/rfm-modeling-customer-audiences/`, pageConfig),
        },
        {
          asset: DefaultThumbnail,
          type: `Recipe`,
          categories: [`Startup`, `Mobile`, `Retail`, `B2B`, `Media`],
          title: `Run Predictive Analytics with SQL Commands`,
          excerpt: `In this recipe, you learn how to use Segment and AWS Redshift ML together to generate predictive analytics without writing any code, spinning up any data pipelines, or requiring significant ML experience.`,
          authorLogo: AuthorLogo,
          authorName: `Made by Segment`,
          button: `Use this recipe`,
          href: getAbsoluteUrl(`/recipes/run-predictive-analytics-with-sql-commands/`, pageConfig),
        },
        {
          asset: CustomerStoryEndeavor,
          type: `Customer Story`,
          title: `How Endeavor used Twilio Segment to Drive Digital Transformation`,
          excerpt: `Endeavor, parent company of IMG, WME, and UFC, turned to Twilio Segment to centralize its data collection, scale its data infrastructure, and share information throughout the company ecosystem. With visibility into customer behaviors, teams use analytics and other downstream tools to ge...`,
          button: `Read their story`,
          href: getAbsoluteUrl(`/customers/endeavor/`, pageConfig),
        },
        {
          asset: CustomerStoryDominos,
          type: `Customer Story`,
          title: `How Domino’s Decreased Cost-Per-Acquisition by 65% with Twilio Segment`,
          excerpt: `Domino’s turned to Twilio Segment to create a universal view of the customer, better visibility of ad campaign effectiveness, and create hyper personalized audiences with Twilio Engage to increase ROAS, revenue and incremental orders across all paid and owned e-commerce channels. Ultimately, Domino's...`,
          button: `Read their story`,
          href: getAbsoluteUrl(`/customers/dominos/`, pageConfig),
        },
        {
          asset: CustomerStoryAllergan,
          type: `Customer Story`,
          title: `How Allergan Built a Loyalty Program to Generate $400 Million in Sales`,
          excerpt: `Allergan built a powerful new intelligent customer engagement architecture with Segment, Twilio, and Snowflake that allows its teams to deliver the right message directly to their patients, and enabled the company to relaunch its personalized customer loyalty program, Allē, resulting in...`,
          button: `Read their story`,
          href: getAbsoluteUrl(`/customers/allergan/`, pageConfig),
        },
      ],
    },
  ]
}

export default data
